/**
 * Pure selection helpers for the approvals triage cockpit (DashboardView).
 *
 * Keyboard triage (J/K/E/X, Shift+A) needs three tiny, deterministic
 * pieces of logic: wrap-around movement, clamping after the list shrinks
 * (an approve/dismiss removes the row, so the item below naturally slides
 * into the freed slot — "advance to next" for free), and a guard so
 * shortcuts never fire while the operator is typing. Kept pure + DOM-free
 * (duck-typed target) so they unit-test under the node environment.
 */

/** Move the selection by one row, wrapping at both ends. Empty list → 0. */
export function moveTriageSelection(
  current: number,
  count: number,
  delta: 1 | -1,
): number {
  if (count <= 0) return 0;
  return (((current + delta) % count) + count) % count;
}

/**
 * Clamp a selection index after the list changes (e.g. a row was resolved
 * and removed). Keeping the same index selects the next row down; when the
 * last row was resolved, fall back to the new last row. Empty list → 0.
 */
export function clampTriageSelection(current: number, count: number): number {
  if (count <= 0) return 0;
  if (current < 0) return 0;
  return Math.min(current, count - 1);
}

/**
 * True when a keydown originated in a text-entry surface (input, textarea,
 * select, or contentEditable) — triage shortcuts must not fire there.
 * Duck-typed so it works on any EventTarget and stays testable in node.
 */
export function isTypingTarget(target: unknown): boolean {
  const el = target as
    | { tagName?: string; isContentEditable?: boolean }
    | null
    | undefined;
  if (!el) return false;
  const tag = typeof el.tagName === 'string' ? el.tagName.toUpperCase() : '';
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    el.isContentEditable === true
  );
}
